
import type { QueriesByCategoryTotalsData } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals-data'
import type { QueriesByCategoryTotalsResponse } from '@empathybroker/tsclient-stats/dist/models/queries-by-category-totals/queries-by-category-totals.response'
import type { TimelySearchResponse } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-response'

import type { PropType } from 'vue'
import type {
  CategoriesDataItem,
  CategoriesTreeItem,
  Counters,
  Dictionary,
  RegularFilterSelectedValues,
} from '../../models'
import type { NoDataMessage } from './no-data.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { FeatureIdEnum, KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'
import { APP_CONSTS } from '../../store/app/app.consts'

import { CONFIG_CONSTS } from '../../store/config/config.consts'

import { FeaturesTreeUtils } from '../../utils'
import GaugeSemicircleChart from '../gauge-semicircle-chart.component.vue'

import Widget from '../widget.component.vue'
import NoData from './no-data.component.vue'

export default defineComponent({
  name: 'CategoriesWidget',
  components: {
    Widget,
    GaugeSemicircleChart,
    NoData,
  },
  mixins: [InsightsMixin],
  props: {
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
    noData: Object as PropType<Dictionary<string>>,
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    title(): string {
      return this.category ? this.messages.titleFiltered : this.messages.title
    },
    feature(): FeatureIdEnum {
      const validCategoryFeature = FeaturesTreeUtils.getFeatureNode(
        FeatureIdEnum.CATEGORIES,
        this.featuresTree[DomainEnum.SEARCH],
      )

      return validCategoryFeature?.routeName as FeatureIdEnum
    },
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.title,
        ...(this.filters && { subtitle: this.noData.filters }),
        ...(this.category && { subtitle: this.noData.category }),
      }
    },
    hasGaugesData(): boolean {
      if (!this.data) {
        return false
      }
      return Object.values(this.gaugeKpis).every((kpi: KpiEnum) => this.getKpiValue(kpi) !== 0)
    },
    gaugeKpis(): KpiEnum[] {
      const gaugeKpis = [...this._kpis]
      if (!this.data?.queryCountPct && this._kpis.includes(KpiEnum.QUERIES)) {
        const queriesIndex = gaugeKpis.findIndex((kpi: KpiEnum) => kpi === KpiEnum.QUERIES)
        gaugeKpis.splice(queriesIndex, 1)
      }
      return gaugeKpis
    },
    _kpis(): KpiEnum[] {
      return [KpiEnum.QUERIES, KpiEnum.FINDABILITY, KpiEnum.ADD2CART].filter((kpi: KpiEnum) =>
        this.kpis.includes(kpi),
      )
    },
    period(): { startDate: Date; endDate: Date } {
      const now = new Date()
      const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      const startDate = new Date(today)
      const endDate = new Date(today)
      startDate.setUTCDate(startDate.getUTCDate() - 7)
      return { startDate, endDate }
    },
    periodString(): string {
      const startDate = new Date(this.period.startDate)
      const endDate = new Date(this.period.endDate)
      endDate.setUTCDate(endDate.getUTCDate() - 1)
      const dateFormatOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: '2-digit',
      }
      const startDateString = startDate.toLocaleDateString(this.$epb.locale, dateFormatOptions)
      const endDateString = endDate.toLocaleDateString(this.$epb.locale, dateFormatOptions)
      return `${startDateString} - ${endDateString}`
    },
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-categories-widget',
      loadingData: true,
      messages: this.$epb.messages.highlights.widget.categories,
    }
  },
  watch: {
    category: {
      async handler(
        newCategory: CategoriesTreeItem,
        oldCategory: CategoriesTreeItem,
      ): Promise<void> {
        if (JSON.stringify(newCategory) !== JSON.stringify(oldCategory)) {
          await this.loadData()
        }
      },
      deep: true,
    },
    filters: {
      async handler(
        newFilters: Record<string, RegularFilterSelectedValues>,
        oldFilters: Record<string, RegularFilterSelectedValues>,
      ): Promise<void> {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          await this.loadData()
        }
      },
      deep: true,
    },
  },
  methods: {
    async fetchData(): Promise<CategoriesDataItem | Counters> {
      this.loadingData = true
      const fetch = this.filters ? this.fetchDataFilters : this.fetchDataCategory
      return await fetch()
    },
    async fetchDataFilters(): Promise<Counters> {
      const params = {
        ...this.period,
        filters: this.filters,
      }
      const response: TimelySearchResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_SEARCH,
        params,
      })

      this.loadingData = false

      return {
        findabilityPct: response.data.findability,
        add2cartCountPct: response.data.add2cartCountPct,
      }
    },
    async fetchDataCategory(): Promise<CategoriesDataItem> {
      const params = {
        ...this.period,
        ...(this.category && { level: this.category.level, parentId: this.category.parentId }),
        rows: 0,
      }
      const response: QueriesByCategoryTotalsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.QUERIES_BY_CATEGORY_TOTALS,
        params,
      })
      if (this.category?.id) {
        const queryCountTotal = response.data.reduce(
          (queryCount: number, categoryItem: QueriesByCategoryTotalsData) => {
            return queryCount + categoryItem.queryCount
          },
          0,
        )
        const categoryFiltered = response.data.find(item => item.categoryId === this.category.id)

        this.loadingData = false

        return {
          ...this.category,
          queryCount: 0,
          queryCountPct: (categoryFiltered.queryCount / queryCountTotal) * 100,
          findabilityPct: categoryFiltered.successCountPct,
          add2cartCountPct: categoryFiltered.add2cartCountPct,
        }
      }
      const accumulativeData = response.data.reduce(
        (accumulative: { successCountPct: number; add2cartCountPct: number }, item) => {
          return {
            successCountPct: accumulative.successCountPct + item.successCountPct,
            add2cartCountPct: accumulative.add2cartCountPct + item.add2cartCountPct,
          }
        },
        { successCountPct: 0, add2cartCountPct: 0 },
      )

      const length = response.data.length || 1

      this.loadingData = false

      return {
        ...this.category,
        findabilityPct: accumulativeData.successCountPct / length,
        add2cartCountPct: accumulativeData.add2cartCountPct / length,
      }
    },
    getKpiLabel(kpi: KpiEnum): string {
      const kpiMessage = this.$epb.messages.insights.analytics.kpis[kpi]
      const labelFn = {
        [KpiEnum.QUERIES]: () => kpiMessage.valueLabel,
        default: () => kpiMessage.percentageLabel,
      }
      return (labelFn[kpi] || labelFn.default)()
    },
    getKpiValue(kpi: KpiEnum): number {
      const value = {
        [KpiEnum.QUERIES]: () => this.data.queryCountPct,
        [KpiEnum.FINDABILITY]: () => this.data.findabilityPct,
        [KpiEnum.ADD2CART]: () => this.data.add2cartCountPct,
      }[kpi]()
      return Math.round(value * 1e2) / 1e2
    },
  },
})
